import * as DropdownPrimitive from '@radix-ui/react-dropdown-menu';
import { Check, ChevronRight } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';

import { cn } from '@/design-system/lib/cn';

import { isActive, type NavGroup } from './navigation';

/** Tolerância para levar o mouse do item até o submenu sem ele fechar no caminho. */
const CLOSE_DELAY_MS = 220;

const ITEM_BASE =
  'relative flex h-10.5 shrink-0 w-full items-center gap-3 rounded-control px-3.5 text-body no-underline transition-colors';

function itemTone(active: boolean) {
  return active
    ? 'bg-sidebar-active font-semibold text-white before:absolute before:inset-y-2.5 before:left-0 before:w-0.75 before:rounded-pill before:bg-primary-focus'
    : 'font-medium text-sidebar-ink hover:bg-sidebar-hover hover:text-white';
}

/**
 * Item de grupo da navegação (Cadastros). No computador, o submenu abre ao lado ao passar
 * o mouse ou com um clique (Enter ou → também; ↑ e ↓ navegam; Esc ou ← fecham e devolvem
 * o foco ao item) — com o menu aberto ou recolhido, e sem passar da borda da tela. No
 * celular não existe passar o mouse: o item abre e fecha a lista ali mesmo, recuada.
 */
export default function NavGroupMenu({
  group,
  labels,
  mobile,
  onNavigate,
}: {
  group: NavGroup;
  /** Mostra os nomes (menu aberto ou gaveta do celular). */
  labels: boolean;
  mobile: boolean;
  onNavigate: () => void;
}) {
  const { pathname } = useLocation();
  const active = isActive(group, pathname);
  const Icon = group.icon;

  if (mobile)
    return <InlineGroup group={group} active={active} pathname={pathname} onNavigate={onNavigate} />;

  return <FlyoutGroup group={group} active={active} labels={labels} pathname={pathname} Icon={Icon} />;
}

function InlineGroup({
  group,
  active,
  pathname,
  onNavigate,
}: {
  group: NavGroup;
  active: boolean;
  pathname: string;
  onNavigate: () => void;
}) {
  const [open, setOpen] = useState(active);
  const Icon = group.icon;
  const listId = `nav-group-${group.label}`;

  return (
    <div className="flex shrink-0 flex-col">
      <button
        type="button"
        aria-expanded={open}
        aria-controls={listId}
        onClick={() => setOpen((o) => !o)}
        className={cn(ITEM_BASE, 'pr-2.5 text-left', itemTone(active))}
      >
        <Icon aria-hidden className="size-4.5 shrink-0 stroke-[1.8]" />
        <span className="flex-1">{group.label}</span>
        <ChevronRight
          aria-hidden
          className={cn('size-4 stroke-[1.8] text-sidebar-muted transition-transform', open && 'rotate-90')}
        />
      </button>
      {open ? (
        <div
          id={listId}
          role="group"
          aria-label={group.label}
          className="mt-0.5 mb-1 ml-5.5 flex shrink-0 flex-col gap-0.5 border-l border-sidebar-card-line pl-2.5"
        >
          {group.items.map((item) => {
            const current = isActive(item, pathname);
            const ItemIcon = item.icon;

            return (
              <Link
                key={item.to}
                to={item.to}
                onClick={onNavigate}
                aria-current={current ? 'page' : undefined}
                className={cn(
                  'flex h-10 shrink-0 items-center gap-2.5 rounded-control-sm px-3 text-control no-underline',
                  current
                    ? 'bg-sidebar-active font-semibold text-white'
                    : 'font-medium text-sidebar-ink hover:bg-sidebar-hover hover:text-white'
                )}
              >
                <ItemIcon aria-hidden className="size-4 shrink-0 stroke-[1.8]" />
                {item.label}
              </Link>
            );
          })}
        </div>
      ) : null}
    </div>
  );
}

function FlyoutGroup({
  group,
  active,
  labels,
  pathname,
  Icon,
}: {
  group: NavGroup;
  active: boolean;
  labels: boolean;
  pathname: string;
  Icon: NavGroup['icon'];
}) {
  const navigate = useNavigate();
  // `hover`: aberto ao passar o mouse (fecha ao sair); `pinned`: por clique ou teclado.
  const [mode, setMode] = useState<'hover' | 'pinned' | null>(null);
  const open = mode !== null;
  const trigger = useRef<HTMLButtonElement>(null);
  const timer = useRef<ReturnType<typeof setTimeout>>(undefined);

  useEffect(() => () => clearTimeout(timer.current), []);

  const cancelClose = () => clearTimeout(timer.current);
  const openByHover = () => {
    cancelClose();
    setMode((m) => m ?? 'hover');
  };
  const closeSoon = () => {
    cancelClose();
    timer.current = setTimeout(() => setMode((m) => (m === 'hover' ? null : m)), CLOSE_DELAY_MS);
  };
  const closeToTrigger = () => {
    setMode(null);
    trigger.current?.focus();
  };

  return (
    <DropdownPrimitive.Root
      modal={false}
      open={open}
      onOpenChange={(next) => {
        cancelClose();
        setMode(next ? 'pinned' : null);
      }}
    >
      <DropdownPrimitive.Trigger asChild>
        <button
          ref={trigger}
          type="button"
          title={labels ? undefined : group.label}
          onPointerEnter={(e) => {
            if (e.pointerType === 'mouse') openByHover();
          }}
          onPointerLeave={(e) => {
            if (e.pointerType === 'mouse') closeSoon();
          }}
          onPointerDown={(e) => {
            // Já aberto pelo mouse: o clique fixa o submenu em vez de fechá-lo.
            if (mode === 'hover') {
              e.preventDefault();
              setMode('pinned');
            }
          }}
          onKeyDown={(e) => {
            if (e.key === 'ArrowRight') {
              e.preventDefault();
              setMode('pinned');
            }
          }}
          className={cn(ITEM_BASE, 'text-left', labels ? 'pr-2.5' : 'justify-center', itemTone(active))}
        >
          <Icon aria-hidden className="size-4.5 shrink-0 stroke-[1.8]" />
          {labels ? (
            <>
              <span className="flex-1">{group.label}</span>
              <ChevronRight aria-hidden className="size-4 stroke-[1.8] text-sidebar-muted" />
            </>
          ) : (
            <span className="sr-only">{group.label}</span>
          )}
        </button>
      </DropdownPrimitive.Trigger>
      <DropdownPrimitive.Portal>
        <DropdownPrimitive.Content
          side="right"
          align="start"
          sideOffset={10}
          collisionPadding={12}
          aria-label={group.label}
          onPointerEnter={cancelClose}
          onPointerLeave={closeSoon}
          // O clique no próprio item é tratado nele (fixa ou fecha), não como clique fora.
          onPointerDownOutside={(e) => {
            if (trigger.current?.contains(e.target as Node)) e.preventDefault();
          }}
          onKeyDown={(e) => {
            if (e.key === 'ArrowLeft') {
              e.preventDefault();
              closeToTrigger();
            }
          }}
          className="z-[80] w-68 rounded-block border border-line-input bg-white p-1.5 shadow-popover outline-none data-[state=open]:animate-in data-[state=open]:fade-in-0"
        >
          <p className="m-0 px-2.5 pt-1.5 pb-2 text-micro font-bold tracking-[0.05em] text-ink-subtle uppercase">
            {group.label}
          </p>
          {group.items.map((item) => {
            const current = isActive(item, pathname);
            const ItemIcon = item.icon;

            return (
              <DropdownPrimitive.Item
                key={item.to}
                onSelect={() => navigate(item.to)}
                aria-current={current ? 'page' : undefined}
                className="flex w-full cursor-pointer items-center gap-2.5 rounded-control-sm px-2.5 py-2 text-left text-ink outline-none select-none data-[highlighted]:bg-surface-muted"
              >
                <span
                  className={cn(
                    'grid size-8 shrink-0 place-items-center rounded-control-sm',
                    current ? 'bg-primary-soft text-primary' : 'bg-surface-muted text-ink-muted'
                  )}
                >
                  <ItemIcon aria-hidden className="size-4 stroke-[1.8]" />
                </span>
                <span className="flex min-w-0 flex-1 flex-col leading-tight">
                  <span className="text-small font-semibold">{item.label}</span>
                  {item.description ? (
                    <span className="truncate text-caption text-ink-subtle">{item.description}</span>
                  ) : null}
                </span>
                {current ? (
                  <Check aria-label="Página atual" className="size-4 stroke-2 text-primary" />
                ) : null}
              </DropdownPrimitive.Item>
            );
          })}
        </DropdownPrimitive.Content>
      </DropdownPrimitive.Portal>
    </DropdownPrimitive.Root>
  );
}
